import { useEffect, useMemo, useRef, useState } from 'react';

import { AddIcon, Button, type TypeSelectOption } from '@thefarmersfront/kitchen';

import type { AgGridReact } from 'ag-grid-react';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import { useModal, useStableCallback } from '~/hooks/common';
import { useGetCollectingRequestDetailMutation } from '~/hooks/delivery-agency/queries';
import { getDeliveryAgencyCollectingRequestsColumnDefs } from '~/utils/delivery-agency/table';

import { DELIVERY_AGENCY_PAGE_SIZE } from '~/constants/delivery-agency';

import CollectingRequestDetailModal from '../CollectingRequestDetailModal';

import type { TypePagination } from '~/types/common.types';
import type { CollectingRequestDetail, CollectingRequestListItem } from '~/types/delivery-agency.types';

import styles from './CollectingRequestTable.module.scss';

const CollectingRequestTable = ({
  data,
  pagination,
  selectedCount,
  onChangePageLimit,
  onChangePage,
  onChangeRowSelection,
  onClickRegister,
  onClickCancel,
}: {
  data: CollectingRequestListItem[] | null;
  pagination: TypePagination;
  selectedCount: number;
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangePage: (value: number) => void;
  onChangeRowSelection: (rows: CollectingRequestListItem[]) => void;
  onClickRegister?: () => void;
  onClickCancel?: () => void;
}) => {
  const detailModal = useModal();
  const [detailData, setDetailData] = useState<CollectingRequestDetail | null>(null);

  const detailMutation = useGetCollectingRequestDetailMutation({ showGlobalDimOverlay: false });

  const handleClickViewDetail = useStableCallback((collectingRequest: CollectingRequestListItem) => {
    detailMutation.mutate(
      { collectingRequestCode: collectingRequest.collectingRequestCode },
      {
        onSuccess: (detail) => {
          setDetailData(detail);
          detailModal.handleOpenModal();
        },
      }
    );
  });

  const { columnDefs } = useMemo(
    () =>
      getDeliveryAgencyCollectingRequestsColumnDefs({
        currentPage: pagination.currentPage - 1,
        pageSize: pagination.pageSize,
        onClickViewDetail: handleClickViewDetail,
      }),
    [pagination.currentPage, pagination.pageSize, handleClickViewDetail]
  );

  const gridRef = useRef<AgGridReact>(null);

  // 같은 조건 재조회처럼 데이터 참조가 그대로면 표가 선택을 유지하므로, 부모 선택이 비면 표 선택도 비운다
  useEffect(() => {
    if (selectedCount === 0) gridRef.current?.api?.deselectAll();
  }, [selectedCount]);

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          <div className={styles['top-right-field']}>
            <Button
              size="tiny"
              variant="outlined"
              color="tertiary"
              status="error"
              disabled={selectedCount === 0 || !onClickCancel}
              onClick={onClickCancel}
            >
              반품 취소
            </Button>
            <Button
              size="tiny"
              color="secondary"
              disabled={!onClickRegister}
              leftIcon={<AddIcon size={16} />}
              onClick={onClickRegister}
            >
              반품 신청
            </Button>
          </div>
        }
      />

      <div className={styles['table-wrapper']}>
        <Table
          gridRef={gridRef}
          data={data}
          options={{ columnDefs }}
          rowStatusType={data === null ? 'INIT' : 'COLLECTING_REQUEST_EMPTY'}
          onSelectionChange={(nodes) =>
            onChangeRowSelection(
              nodes.map((node) => node.data).filter((row): row is CollectingRequestListItem => Boolean(row))
            )
          }
        />
      </div>

      <TableBottomField
        pageSizeOptions={Object.values(DELIVERY_AGENCY_PAGE_SIZE)}
        currentPage={pagination.currentPage - 1}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={(page) => onChangePage(page + 1)}
      />

      {detailModal.isOpen && detailData && (
        <CollectingRequestDetailModal
          open={detailModal.isOpen}
          data={detailData}
          onClose={detailModal.handleCloseModal}
        />
      )}
    </div>
  );
};

export default CollectingRequestTable;
